/**
 * useStockQuotes - 股票行情 Hook
 *
 * 负责从股票洞察卡片中提取股票 ID、获取行情数据、合并到卡片数据中。
 */

import { ref, type Ref } from 'vue-demi';
import type {
  StockQuote,
  MaterialSnapshotData,
} from '@ark/material-checkin-shared';
import type { MaterialCheckinApiClient } from '../../shared/api';
import type { StockInsightCardData } from './useMaterialCards';

/**
 * useStockQuotes Hook 配置
 */
export interface UseStockQuotesOptions {
  /** API 客户端实例 */
  apiClient: MaterialCheckinApiClient;
  /** 是否为英文环境 */
  isEnglish?: boolean | Ref<boolean>;
}

/**
 * useStockQuotes Hook 返回值
 */
export interface UseStockQuotesReturn {
  /** 股票行情映射表 */
  stockQuotesMap: Ref<Map<string, StockQuote>>;
  /** 是否正在加载 */
  loading: Ref<boolean>;
  /** 加载股票行情数据 */
  loadStockQuotes: (materials: MaterialSnapshotData[]) => Promise<void>;
  /** 合并行情数据到卡片 */
  mergeQuotesToCards: (cards: StockInsightCardData[]) => StockInsightCardData[];
}

/**
 * 解包 Ref 或普通值
 */
function unref<T>(value: T | Ref<T>): T {
  return (value as Ref<T>)?.value !== undefined
    ? (value as Ref<T>).value
    : (value as T);
}

/**
 * 从物料列表中提取股票 ID
 */
export function extractStockIds(materials: MaterialSnapshotData[]): string[] {
  const stockIds = new Set<string>();

  for (const material of materials) {
    if (material.componentType !== 'stock_insight_card') continue;
    const stockId = material.displayConfig.stockId as string;
    if (stockId) stockIds.add(stockId);
  }

  return Array.from(stockIds);
}

/**
 * 股票行情 Hook
 */
export function useStockQuotes(
  options: UseStockQuotesOptions,
): UseStockQuotesReturn {
  const { apiClient, isEnglish = false } = options;

  const stockQuotesMap = ref<Map<string, StockQuote>>(new Map());
  const loading = ref(false);

  /**
   * 加载股票行情数据
   */
  async function loadStockQuotes(
    materials: MaterialSnapshotData[],
  ): Promise<void> {
    console.log('[useStockQuotes] 开始加载股票行情');

    const stockIds = extractStockIds(materials);
    console.log('[useStockQuotes] 收集到的 stockIds:', stockIds);

    if (stockIds.length === 0) {
      console.log('[useStockQuotes] 没有股票ID，跳过请求');
      stockQuotesMap.value = new Map();
      return;
    }

    loading.value = true;

    try {
      console.log('[useStockQuotes] 开始请求后端接口');
      const response = await apiClient.getStockQuotes(stockIds);
      console.log('[useStockQuotes] 后端响应:', response);

      const quoteMap = new Map<string, StockQuote>();
      for (const quote of response.quotes || []) {
        if (quote.stockId) {
          quoteMap.set(quote.stockId, quote);
        }
      }

      stockQuotesMap.value = quoteMap;
      console.log('[useStockQuotes] 加载完成，数量:', quoteMap.size);
    } catch (err) {
      console.error('[useStockQuotes] 请求失败:', err);
      stockQuotesMap.value = new Map();
    } finally {
      loading.value = false;
    }
  }

  /**
   * 合并行情数据到卡片
   */
  function mergeQuotesToCards(
    cards: StockInsightCardData[],
  ): StockInsightCardData[] {
    if (stockQuotesMap.value.size === 0) return cards;

    const isEn = unref(isEnglish);

    return cards.map((card) => {
      const quote = stockQuotesMap.value.get(card.stockId);
      if (!quote) return card;

      return {
        ...card,
        symbol: quote.symbol,
        name: isEn ? quote.nameEn : quote.nameCn,
        changePercent: quote.changePercent,
      };
    });
  }

  return {
    stockQuotesMap,
    loading,
    loadStockQuotes,
    mergeQuotesToCards,
  };
}

export default useStockQuotes;
